import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import axios from "axios";
import BASE_URL from "../../url";
import "../styles/ClientCountReport.css";
import { usePersistentFilters } from "../../hooks/usePersistentFilters";
import { Building2, Briefcase, FileText, RotateCcw, TrendingUp, Search, ChevronDown, X, Download } from "lucide-react";
import MissingSubmissionsModal from "./MissingSubmissionsModal";
import ClientSubmissionsModal from "./ClientSubmissionsModal";
import { generateClientReportHtml, generateExecutiveDeckHtml } from "../utils/reportExportUtils";
import {
  BarChart, Bar, AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip,
  Legend, ResponsiveContainer, LabelList
} from "recharts";

// ── Palette ───────────────────────────────────
const COLORS = [
  '#009688', '#3b82f6', '#f59e0b', '#ec4899',
  '#8b5cf6', '#ef4444', '#14b8a6', '#f97316',
  '#84cc16', '#06b6d4', '#6366f1'
];

const getWeekNumber = (d) => {
  const date = new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate()));
  const dayNum = date.getUTCDay() || 7;
  date.setUTCDate(date.getUTCDate() + 4 - dayNum);
  const yearStart = new Date(Date.UTC(date.getUTCFullYear(), 0, 1));
  return Math.ceil((((date - yearStart) / 86400000) + 1) / 7);
};

const formatDateKey = (dateKey, tp) => {
  if (!dateKey) return "";
  const d = new Date(dateKey + "T00:00:00Z");
  if (tp === "daily")   return d.toLocaleDateString("en-US", { timeZone: "UTC", month: "short", day: "numeric" });
  if (tp === "weekly")  return "W" + getWeekNumber(d) + " (" + d.toLocaleDateString("en-US", { timeZone: "UTC", month: "short", day: "numeric" }) + ")";
  if (tp === "monthly") return d.toLocaleDateString("en-US", { timeZone: "UTC", month: "short", year: "numeric" });
  if (tp === "yearly")  return String(d.getUTCFullYear());
  return dateKey;
};

// ─── Searchable Client Dropdown Component ─────────────────────────────────────
const SearchableClientSelect = ({ clients, selectedClient, onSelect }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState("");
  const containerRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const filteredClients = (clients || []).filter(c =>
    (c.clientName || "").toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="ccr-input-group" ref={containerRef} style={{ position: "relative" }}>
      <label>Client</label>
      <div
        onClick={() => setIsOpen(prev => !prev)}
        style={{
          display: "flex", alignItems: "center", justifyContent: "space-between",
          padding: "7px 12px", background: "white", border: "1px solid #cbd5e1",
          borderRadius: "6px", cursor: "pointer", fontSize: "13px", color: "#1e293b",
          minWidth: "180px", userSelect: "none", height: "36px", boxSizing: "border-box"
        }}
      >
        <span style={{ fontWeight: selectedClient ? 600 : 400, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {selectedClient ? selectedClient.name : "All Clients"}
        </span>
        <ChevronDown size={14} color="#64748b" style={{ transform: isOpen ? "rotate(180deg)" : "rotate(0deg)", transition: "transform 0.2s", marginLeft: "8px", flexShrink: 0 }} />
      </div>

      {isOpen && (
        <div
          style={{
            position: "absolute", top: "100%", left: 0, marginTop: "4px",
            width: "220px", background: "white", border: "1px solid #cbd5e1",
            borderRadius: "8px", boxShadow: "0 10px 25px rgba(0,0,0,0.15)",
            zIndex: 99999, overflow: "hidden"
          }}
        >
          {/* Search Input Box */}
          <div style={{ padding: "8px", borderBottom: "1px solid #f1f5f9", display: "flex", alignItems: "center", gap: "6px", background: "#f8fafc" }}>
            <Search size={14} color="#94a3b8" />
            <input
              type="text"
              autoFocus
              placeholder="Search client…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              style={{
                width: "100%", border: "none", outline: "none", background: "transparent",
                fontSize: "12px", color: "#1e293b"
              }}
            />
            {search && (
              <X size={12} color="#94a3b8" style={{ cursor: "pointer" }} onClick={() => setSearch("")} />
            )}
          </div>

          {/* Options List */}
          <div style={{ maxHeight: "200px", overflowY: "auto" }}>
            <div
              onClick={() => { onSelect(null); setIsOpen(false); setSearch(""); }}
              style={{
                padding: "8px 12px", fontSize: "12px", cursor: "pointer",
                background: !selectedClient ? "#f0fdf4" : "transparent",
                color: !selectedClient ? "#166534" : "#334155",
                fontWeight: !selectedClient ? 600 : 400
              }}
            >
              All Clients
            </div>
            {filteredClients.length === 0 ? (
              <div style={{ padding: "10px", fontSize: "12px", color: "#94a3b8", textAlign: "center" }}>No clients found</div>
            ) : (
              filteredClients.map(c => {
                const isSelected = selectedClient?.name === c.clientName;
                return (
                  <div
                    key={c.clientName}
                    onClick={() => { onSelect(c.clientName); setIsOpen(false); setSearch(""); }}
                    style={{
                      padding: "8px 12px", fontSize: "12px", cursor: "pointer",
                      background: isSelected ? "#e0f2fe" : "transparent",
                      color: isSelected ? "#0369a1" : "#334155",
                      fontWeight: isSelected ? 600 : 400
                    }}
                  >
                    {c.clientName}
                  </div>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
};

const toYMD = (d) => {
  if (!d) return "";
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const ClientCountReport = () => {
  const todayVal = toYMD(new Date());

  const initialFilters = {
    startDate: todayVal,
    endDate: todayVal,
    dropdownSelection: "today",
    creationType: "all",
    trendPeriod: "daily",
    regionFilter: "onsite",
    selectedClient: null,
    activePeriod: null,
    clientFilterStatus: "all"
  };

  const { filters, setFilters, handleClearFilter } = usePersistentFilters("clientCountReportFilters2", initialFilters);
  const startDate          = filters.startDate          || todayVal;
  const endDate            = filters.endDate            || todayVal;
  const dropdownSelection  = filters.dropdownSelection  || "today";
  const creationType       = filters.creationType       || "all";
  const trendPeriod        = filters.trendPeriod        || "daily";
  const regionFilter       = filters.regionFilter       || "onsite";
  const selectedClient     = filters.selectedClient     || null;
  const activePeriod       = filters.activePeriod       || null;
  const clientFilterStatus = filters.clientFilterStatus || "all";

  const setStartDate          = (v) => setFilters(p => ({ ...p, startDate: v }));
  const setEndDate            = (v) => setFilters(p => ({ ...p, endDate: v }));
  const setDropdownSel        = (v) => setFilters(p => ({ ...p, dropdownSelection: v }));
  const setCreationType       = (v) => setFilters(p => ({ ...p, creationType: v }));
  const setTrendPeriod        = (v) => setFilters(p => ({ ...p, trendPeriod: v }));
  const setRegionFilter       = (v) => setFilters(p => ({ ...p, regionFilter: v }));
  const setSelectedClient     = (v) => setFilters(p => ({ ...p, selectedClient: typeof v === 'function' ? v(p.selectedClient) : v }));
  const setActivePeriod       = (v) => setFilters(p => ({ ...p, activePeriod: typeof v === 'function' ? v(p.activePeriod) : v }));
  const setClientFilterStatus = (v) => setFilters(p => ({ ...p, clientFilterStatus: typeof v === 'function' ? v(p.clientFilterStatus || 'all') : v }));

  // Data
  const [clients,   setClients]   = useState([]);
  const [totals,    setTotals]    = useState({ clientCount: 0, jobCount: 0, submissionCount: 0 });
  const [trendData, setTrendData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error,     setError]     = useState("");
  const [isReportDropdownOpen, setIsReportDropdownOpen] = useState(false);
  const reportDropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (reportDropdownRef.current && !reportDropdownRef.current.contains(e.target)) {
        setIsReportDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleDownloadReport = async (reportType) => {
    setIsReportDropdownOpen(false);

    if (reportType === 'standard') {
      generateClientReportHtml({
        startDate,
        endDate,
        region: regionFilter,
        creationType,
        clientFilterStatus,
        displayedTotals,
        clients: sortedClients
      });
      return;
    }

    const today = new Date();
    let repStart = startDate;
    let repEnd = endDate;

    if (reportType === 'today') {
      repStart = toYMD(today);
      repEnd = toYMD(today);
    } else if (reportType === 'weekly') {
      const day = today.getDay();
      const diff = today.getDate() - day + (day === 0 ? -6 : 1);
      const startOfWeek = new Date(today.getFullYear(), today.getMonth(), diff);
      const endOfWeek = new Date(today.getFullYear(), today.getMonth(), diff + 4);
      repStart = toYMD(startOfWeek);
      repEnd = toYMD(endOfWeek);
    } else if (reportType === 'monthly') {
      const startOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
      repStart = toYMD(startOfMonth);
      repEnd = toYMD(today);
    } else if (reportType === 'custom') {
      repStart = startDate;
      repEnd = endDate;
    }

    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${BASE_URL}/api/recruitment/applications/job-report/executive-data`, {
        params: {
          startDate: repStart,
          endDate: repEnd,
          region: regionFilter,
          client: selectedClient || 'all'
        },
        headers: { Authorization: `Bearer ${token}` }
      });
      const execData = res.data || {};
      generateExecutiveDeckHtml({
        startDate: repStart,
        endDate: repEnd,
        regionFilter,
        selectedClient: selectedClient || 'All Clients',
        summary: execData.summary || {
          totalOpenJobs: displayedTotals.jobCount || 0,
          totalSubmissions: displayedTotals.submissionCount || 0,
          totalInterviews: 0,
          totalClosures: 0
        },
        podData: execData.podData || [],
        keyClosures: execData.keyClosures || [],
        interviewsAwaiting: execData.interviewsAwaiting || [],
        onboardingPipeline: execData.onboardingPipeline || {}
      });
    } catch (err) {
      console.warn("Could not fetch executive data, fallback:", err);
      generateExecutiveDeckHtml({
        startDate: repStart,
        endDate: repEnd,
        regionFilter,
        selectedClient: selectedClient || 'All Clients',
        summary: {
          totalOpenJobs: displayedTotals.jobCount || 0,
          totalSubmissions: displayedTotals.submissionCount || 0,
          totalInterviews: 0,
          totalClosures: 0
        },
        podData: []
      });
    }
  };


  // ─── Fetch ──────────────────────────────────────────────────────────────────
  const fetchData = useCallback(async (oStart, oEnd, oCr, oRegion, oTp) => {
    const sDate  = oStart  !== undefined ? oStart  : startDate;
    const eDate  = oEnd    !== undefined ? oEnd    : endDate;
    const cr     = oCr     !== undefined ? oCr     : creationType;
    const region = oRegion !== undefined ? oRegion : regionFilter;
    const tp     = oTp     !== undefined ? oTp     : trendPeriod;
    try {
      setIsLoading(true);
      setError("");
      const token = localStorage.getItem("token");
      const res = await axios.get(`${BASE_URL}/api/jobs/stats/client-counts`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { startDate: sDate, endDate: eDate, creationType: cr, region, trendPeriod: tp }
      });
      if (res.data.success) {
        setClients(res.data.clients || []);
        setTotals(res.data.totals  || { clientCount: 0, jobCount: 0, submissionCount: 0 });
        setTrendData(res.data.trendData || []);
      }
    } catch (err) {
      console.error("Error fetching client counts:", err);
      setError("Failed to load client count data.");
    } finally {
      setIsLoading(false);
    }
  }, [startDate, endDate, creationType, regionFilter, trendPeriod]);

  useEffect(() => {
    fetchData(startDate, endDate, creationType, regionFilter, trendPeriod);
    // eslint-disable-next-line
  }, []);

  // Re-fetch when region changes
  useEffect(() => {
    fetchData(startDate, endDate, creationType, regionFilter, trendPeriod);
    // eslint-disable-next-line
  }, [regionFilter]);


  // ─── Dropdown date helper ────────────────────────────────────────────────────
  const handleDropdownChange = (e) => {
    const val = e.target.value;
    setDropdownSel(val);
    setActivePeriod(null);
    if (val === "custom") return;
    const today = new Date();
    let start = new Date(today);
    let end = new Date(today);

    if (val === "today") {
      start = new Date(today);
      end = new Date(today);
    } else if (val === "yesterday") {
      start = new Date(today.getFullYear(), today.getMonth(), today.getDate() - 1);
      end = new Date(today.getFullYear(), today.getMonth(), today.getDate() - 1);
    } else if (val === "this_week") {
      const day = today.getDay();
      const diff = today.getDate() - day + (day === 0 ? -6 : 1);
      start = new Date(today.getFullYear(), today.getMonth(), diff);
      end = new Date(today);
    } else if (val === "last_week") {
      const day = today.getDay();
      const diff = today.getDate() - day + (day === 0 ? -6 : 1) - 7;
      start = new Date(today.getFullYear(), today.getMonth(), diff);
      end = new Date(today.getFullYear(), today.getMonth(), diff + 6);
    } else if (val === "this_month") {
      start = new Date(today.getFullYear(), today.getMonth(), 1);
      end = new Date(today);
    } else if (val === "last_month") {
      start = new Date(today.getFullYear(), today.getMonth() - 1, 1);
      end = new Date(today.getFullYear(), today.getMonth(), 0);
    } else if (val === "this_quarter") {
      const q = Math.floor(today.getMonth() / 3);
      start = new Date(today.getFullYear(), q * 3, 1);
      end = new Date(today);
    } else if (val === "last_quarter") {
      const q = Math.floor(today.getMonth() / 3) - 1;
      start = new Date(today.getFullYear(), q * 3, 1);
      end = new Date(today.getFullYear(), (q + 1) * 3, 0);
    }

    const ns = toYMD(start);
    const ne = toYMD(end);
    setStartDate(ns); setEndDate(ne);
    fetchData(ns, ne, creationType, regionFilter, trendPeriod);
  };

  // ─── Period buttons handler (Daily / Weekly / Monthly / Yearly) ──────────────
  const handlePeriodChange = (period) => {
    setActivePeriod(period);
    setTrendPeriod(period);
    const today = new Date();
    let start = new Date(today);

    if (period === "daily")        start.setDate(today.getDate() - 30);
    else if (period === "weekly")  start.setMonth(today.getMonth() - 3);
    else if (period === "monthly") start.setFullYear(today.getFullYear() - 1);
    else                           start.setFullYear(today.getFullYear() - 5);

    const sDate = toYMD(start);
    const eDate = toYMD(today);
    setStartDate(sDate); setEndDate(eDate); setDropdownSel("custom");
    fetchData(sDate, eDate, creationType, regionFilter, period);
  };

  // ─── Handle client click (chart bar or legend) ────────────────────────────
  const handleClientClick = (clientName) => {
    if (selectedClient && selectedClient.name === clientName) {
      setSelectedClient(null);
    } else {
      setSelectedClient({ name: clientName });
    }
  };

  // ─── Chart data from trendData ─────────────────────
  const { chartData, chartKeys } = useMemo(() => {
    if (!trendData || trendData.length === 0) return { chartData: [], chartKeys: [] };

    const trendMap = {};
    const clientTotals = {};

    trendData.forEach(item => {
      const groupKey = item.dateKey;
      const displayStr = formatDateKey(groupKey, trendPeriod);
      
      if (!trendMap[groupKey]) {
        trendMap[groupKey] = { name: displayStr, _sort: groupKey, totalJobs: 0, totalSubs: 0 };
      }
      
      const clientName = item.clientName || "Unknown";
      const subs = item.submissions || 0;
      const jobs = item.jobs || 0;

      trendMap[groupKey][clientName] = (trendMap[groupKey][clientName] || 0) + subs;
      trendMap[groupKey][`${clientName}_jobs`] = (trendMap[groupKey][`${clientName}_jobs`] || 0) + jobs;
      
      trendMap[groupKey].totalJobs += jobs;
      trendMap[groupKey].totalSubs += subs;

      clientTotals[clientName] = (clientTotals[clientName] || 0) + subs;
    });

    const sortedKeys = Object.keys(clientTotals).sort((a, b) => clientTotals[b] - clientTotals[a]);
    const sortedData = Object.values(trendMap).sort((a, b) => new Date(a._sort) - new Date(b._sort));
    return { chartData: sortedData, chartKeys: sortedKeys };
  }, [trendData, trendPeriod]);

  // ─── Total Client Trend line data (cumulative total jobs & submissions per date) ───
  const totalClientTrendData = useMemo(() => {
    if (!chartData || chartData.length === 0) return [];
    return chartData.map(d => {
      let jobs = 0;
      let subs = 0;
      if (selectedClient) {
        jobs = d[`${selectedClient.name}_jobs`] || 0;
        subs = d[selectedClient.name] || 0;
      } else {
        jobs = d.totalJobs || 0;
        subs = d.totalSubs || 0;
      }
      return {
        name: d.name,
        jobs,
        submissions: subs
      };
    });
  }, [chartData, selectedClient]);

  const [missingModalClient, setMissingModalClient] = useState(null); // client name string or 'all' or null
  const [submittedModalClient, setSubmittedModalClientState] = useState(() => {
    try {
      const sp = new URLSearchParams(window.location.search);
      return sp.get('clientModal') || null;
    } catch (e) {
      return null;
    }
  });

  const setSubmittedModalClient = useCallback((clientName) => {
    setSubmittedModalClientState(clientName);
    try {
      const url = new URL(window.location.href);
      if (clientName) {
        url.searchParams.set('clientModal', clientName);
      } else {
        url.searchParams.delete('clientModal');
      }
      window.history.replaceState({}, '', url.toString());
    } catch (e) {
      console.error('Failed to update URL state:', e);
    }
  }, []);

  useEffect(() => {
    const onPopState = () => {
      try {
        const sp = new URLSearchParams(window.location.search);
        setSubmittedModalClientState(sp.get('clientModal') || null);
      } catch (e) {
        // ignore
      }
    };
    window.addEventListener('popstate', onPopState);
    return () => window.removeEventListener('popstate', onPopState);
  }, []);

  // ─── Filter counts ───
  const clientsWithSubsCount = useMemo(() => (clients || []).filter(c => ((c.submittedToClientCount !== undefined ? c.submittedToClientCount : c.submissionCount) || 0) > 0).length, [clients]);
  const clientsWithoutSubsCount = useMemo(() => (clients || []).filter(c => ((c.submittedToClientCount !== undefined ? c.submittedToClientCount : c.submissionCount) || 0) === 0).length, [clients]);

  // ─── Sorted and filtered clients for Detailed Breakdown table ───
  const sortedClients = useMemo(() => {
    if (!clients || clients.length === 0) return [];
    let list = [...clients];
    if (clientFilterStatus === 'with_subs') {
      list = list.filter(c => ((c.submittedToClientCount !== undefined ? c.submittedToClientCount : c.submissionCount) || 0) > 0);
    } else if (clientFilterStatus === 'without_subs') {
      list = list.filter(c => ((c.submittedToClientCount !== undefined ? c.submittedToClientCount : c.submissionCount) || 0) === 0);
    }
    return list.sort((a, b) => (b.submittedToClientCount ?? 0) - (a.submittedToClientCount ?? 0) || (b.submissionCount ?? 0) - (a.submissionCount ?? 0));
  }, [clients, clientFilterStatus]);

  // ─── Displayed Totals for Top Stat Cards (filters to selected client if one is selected) ───
  const displayedTotals = useMemo(() => {
    if (selectedClient) {
      const clientRow = clients.find(c => (c.clientName || '').toLowerCase() === selectedClient.name.toLowerCase());
      return {
        clientCount: clientRow ? 1 : 0,
        clientSub: `Selected: ${selectedClient.name}`,
        jobCount: clientRow ? (clientRow.jobCount ?? 0) : 0,
        jobSub: `Openings for ${selectedClient.name}`,
        submissionCount: clientRow ? (clientRow.submissionCount ?? 0) : 0,
        submittedToClientCount: clientRow ? (clientRow.submittedToClientCount ?? 0) : 0,
        subSub: `Total sourced for ${selectedClient.name}`
      };
    }
    return {
      clientCount: totals.clientCount ?? 0,
      clientSub: "Unique clients in period",
      jobCount: totals.jobCount ?? 0,
      jobSub: "Job openings in period",
      submissionCount: totals.submissionCount ?? 0,
      submittedToClientCount: totals.submittedToClientCount ?? 0,
      subSub: "Total sourced across all clients"
    };
  }, [selectedClient, clients, totals]);

  const isFiltered = dropdownSelection !== "today" || startDate !== todayVal || endDate !== todayVal || creationType !== "all" || selectedClient !== null;

  // ─── Region button style ─────────────────────────────────────────────────────
  const regionBtnStyle = (r) => {
    const active = regionFilter === r;
    return {
      padding: "6px 16px",
      borderRadius: "20px",
      border: active ? "1px solid #3b5bfd" : "1px solid #e2e8f0",
      background: active ? "#3b5bfd" : "#fff",
      color: active ? "#fff" : "#64748b",
      fontSize: "13px",
      fontWeight: "500",
      cursor: "pointer",
      transition: "all 0.2s ease"
    };
  };

  // ─── Render ─────────────────────────────────────────────────────────────────
  return (
    <div className="ccr-container" style={{ padding: "10px", background: "#f8f9fa", minHeight: "100vh", boxSizing: "border-box" }}>
      {/* Page Title & Onsite/Offshore Toggle in Top Corner */}
      <div className="ccr-header-section" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
        <h2 className="ccr-title">Client Count</h2>
        {/* Onsite / Offshore Toggle */}
        <div style={{ display: 'flex', gap: '8px' }}>
          <button style={regionBtnStyle("onsite")} onClick={() => setRegionFilter("onsite")}>Onsite</button>
          <button style={regionBtnStyle("offshore")} onClick={() => setRegionFilter("offshore")}>Offshore</button>
        </div>
      </div>

      {/* ── Filters Bar ── */}
      <div className="ccr-filters">
        <p className="ccr-filter-label">Date filters:</p>
        <div className="ccr-filter-row">
          <div className="ccr-input-group">
            <label>Quick Select</label>
            <select value={dropdownSelection} onChange={handleDropdownChange}>
              <option value="today">Today</option>
              <option value="yesterday">Yesterday</option>
              <option value="this_week">This Week</option>
              <option value="last_week">Last Week</option>
              <option value="this_month">This Month</option>
              <option value="last_month">Last Month</option>
              <option value="this_quarter">This Quarter</option>
              <option value="last_quarter">Last Quarter</option>
              <option value="custom">Custom Range</option>
            </select>
          </div>

          <div className="ccr-input-group">
            <label>Start date</label>
            <input type="date" value={startDate} onChange={(e) => { setActivePeriod(null); setStartDate(e.target.value); setDropdownSel("custom"); }} />
          </div>

          <div className="ccr-input-group">
            <label>End date</label>
            <input type="date" value={endDate} onChange={(e) => { setActivePeriod(null); setEndDate(e.target.value); setDropdownSel("custom"); }} />
          </div>

          <button className="ccr-apply-btn" onClick={() => fetchData()} disabled={isLoading}>
            {isLoading ? "Loading..." : "Apply"}
          </button>

          {/* Searchable Client Filter Dropdown */}
          <SearchableClientSelect
            clients={sortedClients}
            selectedClient={selectedClient}
            onSelect={(name) => {
              if (!name) {
                setSelectedClient(null);
              } else {
                setSelectedClient({ name });
              }
            }}
          />

          {/* Creation Source */}
          <div className="ccr-input-group">
            <label>Creation Source</label>
            <select value={creationType} onChange={(e) => { const v=e.target.value; setCreationType(v); fetchData(startDate, endDate, v, regionFilter, trendPeriod); }}>
              <option value="all">All (Automation &amp; Manual)</option>
              <option value="automation">Automation</option>
              <option value="manual">Manual</option>
            </select>
          </div>

          {/* Download Report Dropdown */}
          <div style={{ position: 'relative' }} ref={reportDropdownRef}>
            <button
              onClick={() => setIsReportDropdownOpen(prev => !prev)}
              title="Download Report Options"
              style={{
                padding: "6px 14px",
                borderRadius: "6px",
                border: "1px solid #3b5bfd",
                background: isReportDropdownOpen ? "#3b5bfd" : "#ffffff",
                color: isReportDropdownOpen ? "#ffffff" : "#3b5bfd",
                cursor: "pointer",
                fontSize: "13px",
                fontWeight: 600,
                display: "flex",
                alignItems: "center",
                gap: "6px",
                height: "36px",
                alignSelf: "flex-end",
                boxShadow: "0 1px 2px rgba(0,0,0,0.05)",
                transition: "all 0.15s ease"
              }}
              onMouseEnter={(e) => { if (!isReportDropdownOpen) e.currentTarget.style.background = "#eff6ff"; }}
              onMouseLeave={(e) => { if (!isReportDropdownOpen) e.currentTarget.style.background = "#ffffff"; }}
            >
              <Download size={14} /> Download Report <ChevronDown size={14} style={{ transform: isReportDropdownOpen ? "rotate(180deg)" : "none", transition: "transform 0.2s" }} />
            </button>

            {isReportDropdownOpen && (
              <div style={{
                position: 'absolute',
                top: '100%',
                right: 0,
                marginTop: '6px',
                background: '#ffffff',
                borderRadius: '8px',
                border: '1px solid #e2e8f0',
                boxShadow: '0 10px 25px -5px rgba(0,0,0,0.15), 0 8px 10px -6px rgba(0,0,0,0.1)',
                zIndex: 1000,
                minWidth: '240px',
                overflow: 'hidden'
              }}>
                <div style={{ padding: '8px 12px', fontSize: '11px', fontWeight: '700', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.5px', background: '#f8fafc', borderBottom: '1px solid #f1f5f9' }}>
                  Select Report Format
                </div>

                <button
                  type="button"
                  onClick={() => handleDownloadReport('today')}
                  style={{
                    width: '100%',
                    padding: '10px 14px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    background: 'none',
                    border: 'none',
                    textAlign: 'left',
                    fontSize: '13px',
                    color: '#1e293b',
                    cursor: 'pointer',
                    borderBottom: '1px solid #f8fafc',
                    transition: 'background 0.15s'
                  }}
                  onMouseEnter={(e) => { e.currentTarget.style.background = '#eff6ff'; e.currentTarget.style.color = '#3b5bfd'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.background = 'none'; e.currentTarget.style.color = '#1e293b'; }}
                >
                  <span>⚡</span>
                  <div>
                    <div style={{ fontWeight: '600' }}>Today</div>
                    <div style={{ fontSize: '11px', color: '#64748b' }}>Single day client activity snapshot</div>
                  </div>
                </button>

                <button
                  type="button"
                  onClick={() => handleDownloadReport('weekly')}
                  style={{
                    width: '100%',
                    padding: '10px 14px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    background: 'none',
                    border: 'none',
                    textAlign: 'left',
                    fontSize: '13px',
                    color: '#0f172a',
                    cursor: 'pointer',
                    borderBottom: '1px solid #f8fafc',
                    transition: 'background 0.15s'
                  }}
                  onMouseEnter={(e) => { e.currentTarget.style.background = '#eff6ff'; e.currentTarget.style.color = '#3b5bfd'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.background = 'none'; e.currentTarget.style.color = '#0f172a'; }}
                >
                  <span>📊</span>
                  <div>
                    <div style={{ fontWeight: '700', color: '#3b5bfd' }}>Weekly (Executive Deck)</div>
                    <div style={{ fontSize: '11px', color: '#64748b' }}>9-slide PPTX-style review (Mon–Fri)</div>
                  </div>
                </button>

                <button
                  type="button"
                  onClick={() => handleDownloadReport('monthly')}
                  style={{
                    width: '100%',
                    padding: '10px 14px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    background: 'none',
                    border: 'none',
                    textAlign: 'left',
                    fontSize: '13px',
                    color: '#1e293b',
                    cursor: 'pointer',
                    borderBottom: '1px solid #f8fafc',
                    transition: 'background 0.15s'
                  }}
                  onMouseEnter={(e) => { e.currentTarget.style.background = '#eff6ff'; e.currentTarget.style.color = '#3b5bfd'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.background = 'none'; e.currentTarget.style.color = '#1e293b'; }}
                >
                  <span>🗓️</span>
                  <div>
                    <div style={{ fontWeight: '600' }}>Monthly</div>
                    <div style={{ fontSize: '11px', color: '#64748b' }}>Current month performance</div>
                  </div>
                </button>

                <button
                  type="button"
                  onClick={() => handleDownloadReport('custom')}
                  style={{
                    width: '100%',
                    padding: '10px 14px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    background: 'none',
                    border: 'none',
                    textAlign: 'left',
                    fontSize: '13px',
                    color: '#1e293b',
                    cursor: 'pointer',
                    borderBottom: '1px solid #f8fafc',
                    transition: 'background 0.15s'
                  }}
                  onMouseEnter={(e) => { e.currentTarget.style.background = '#eff6ff'; e.currentTarget.style.color = '#3b5bfd'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.background = 'none'; e.currentTarget.style.color = '#1e293b'; }}
                >
                  <span>⚙️</span>
                  <div>
                    <div style={{ fontWeight: '600' }}>Selected Date Range</div>
                    <div style={{ fontSize: '11px', color: '#64748b' }}>{startDate} — {endDate}</div>
                  </div>
                </button>

                <button
                  type="button"
                  onClick={() => handleDownloadReport('standard')}
                  style={{
                    width: '100%',
                    padding: '10px 14px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    background: 'none',
                    border: 'none',
                    textAlign: 'left',
                    fontSize: '13px',
                    color: '#1e293b',
                    cursor: 'pointer',
                    transition: 'background 0.15s'
                  }}
                  onMouseEnter={(e) => { e.currentTarget.style.background = '#eff6ff'; e.currentTarget.style.color = '#3b5bfd'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.background = 'none'; e.currentTarget.style.color = '#1e293b'; }}
                >
                  <span>🏢</span>
                  <div>
                    <div style={{ fontWeight: '600' }}>Standard Client Report</div>
                    <div style={{ fontSize: '11px', color: '#64748b' }}>Client-wise volume & delivery analytics (PDF/Print)</div>
                  </div>
                </button>
              </div>
            )}
          </div>

          {/* Reset / Clear All Filters */}
          {isFiltered && (
            <button
              className="ccr-reset-btn"
              onClick={() => {
                handleClearFilter();
                setStartDate(todayVal); setEndDate(todayVal); setDropdownSel("today"); setCreationType("all"); setTrendPeriod("daily"); setSelectedClient(null); setActivePeriod(null);
                fetchData(todayVal, todayVal, "all", regionFilter, "daily");
              }}
              style={{
                display: "flex", alignItems: "center", gap: "6px",
                padding: "7px 14px", background: "#fef2f2", color: "#dc2626",
                border: "1px solid #fca5a5", borderRadius: "6px", cursor: "pointer",
                fontWeight: 600, fontSize: "13px", height: "36px", alignSelf: "flex-end"
              }}
            >
              <RotateCcw size={14}/> ✕ Clear All Filters
            </button>
          )}
        </div>
      </div>

      {error && <p style={{color:"#ef4444",marginBottom:16}}>{error}</p>}

      {/* ── Stat Cards ── */}
      <div className="ccr-stats-grid">
        <div className="ccr-stat-card clients">
          <div className="ccr-card-top">
            <span className="ccr-card-label">Total Clients</span>
            <span className="ccr-card-icon"><Building2 size={16}/></span>
          </div>
          <div className="ccr-card-number">{isLoading ? "…" : (displayedTotals.clientCount ?? 0)}</div>
          <div className="ccr-card-sub">{displayedTotals.clientSub}</div>
        </div>

        <div className="ccr-stat-card jobs">
          <div className="ccr-card-top">
            <span className="ccr-card-label">Total Jobs</span>
            <span className="ccr-card-icon"><Briefcase size={16}/></span>
          </div>
          <div className="ccr-card-number">{isLoading ? "…" : (displayedTotals.jobCount ?? 0)}</div>
          <div className="ccr-card-sub">{displayedTotals.jobSub}</div>
        </div>

        <div className="ccr-stat-card subs">
          <div className="ccr-card-top">
            <span className="ccr-card-label">Total Sourced</span>
            <span className="ccr-card-icon"><FileText size={16}/></span>
          </div>
          <div className="ccr-card-number">{isLoading ? "…" : (displayedTotals.submissionCount ?? 0)}</div>
          <div className="ccr-card-sub">{displayedTotals.subSub}</div>
        </div>

        <div
          className="ccr-stat-card clientsubs"
          style={{
            borderLeftColor: '#3b5bfd',
            cursor: (displayedTotals.submittedToClientCount ?? 0) > 0 ? 'pointer' : 'default'
          }}
          onClick={() => {
            if ((displayedTotals.submittedToClientCount ?? 0) > 0) {
              setSubmittedModalClient(selectedClient ? selectedClient.name : 'all');
            }
          }}
          title={(displayedTotals.submittedToClientCount ?? 0) > 0 ? `Click to view candidates submitted to ${selectedClient ? selectedClient.name : 'all clients'}` : ''}
        >
          <div className="ccr-card-top">
            <span className="ccr-card-label" style={{ color: '#0f766e' }}>Submitted to Client</span>
            <span className="ccr-card-icon" style={{ background: '#ccfbf1', color: '#0f766e' }}><TrendingUp size={16}/></span>
          </div>
          <div className="ccr-card-number" style={{ color: '#0f766e' }}>{isLoading ? "…" : (displayedTotals.submittedToClientCount ?? 0)}</div>
          <div className="ccr-card-sub">{selectedClient ? `Client subs for ${selectedClient.name}` : 'Candidates in client pipeline'}</div>
        </div>
      </div>

      {/* ── Chart Card ── */}
      <div className="ccr-chart-card">
        <div className="ccr-chart-header" style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <div className="ccr-chart-title" style={{ display: "flex", alignItems: "center", gap: "8px", flexWrap: "wrap" }}>
            <Building2 size={16} color="#3b5bfd" />
            Client Submissions Trend &mdash;&nbsp;
            <span className="ccr-chart-subtitle">
              {trendPeriod ? `${trendPeriod.charAt(0).toUpperCase() + trendPeriod.slice(1)} View` : 'Daily View'}
              {selectedClient ? ` — ${selectedClient.name}` : ' (Top Clients)'}
            </span>
          </div>
          {/* Daily / Weekly / Monthly / Yearly period buttons */}
          <div className="ccr-period-btns" style={{ padding: "0 4px" }}>
            {["daily","weekly","monthly","yearly"].map(p=>(
              <button key={p} className={"ccr-period-btn"+(activePeriod===p?" active":"")} onClick={()=>handlePeriodChange(p)}>
                {p.charAt(0).toUpperCase()+p.slice(1)}
              </button>
            ))}
          </div>
        </div>

        {/* ── Selected Client Jobs & Submissions Summary Pills ── */}
        {selectedClient && (() => {
          const clientRow = clients.find(c => (c.clientName || '').toLowerCase() === selectedClient.name.toLowerCase());
          const jCount = clientRow ? (clientRow.jobCount ?? 0) : 0;
          const sCount = clientRow ? (clientRow.submissionCount ?? 0) : 0;
          const color  = COLORS[chartKeys.indexOf(selectedClient.name) % COLORS.length] || '#3b5bfd';

          return (
            <div style={{
              display: 'flex', alignItems: 'center', gap: '12px',
              padding: '10px 16px', background: '#f8fafc', borderRadius: '8px',
              border: '1px solid #e2e8f0', margin: '0 16px 12px 16px', flexWrap: 'wrap'
            }}>
              <span style={{ fontWeight: 700, color: '#1e293b', fontSize: '13px' }}>
                {selectedClient.name} ({startDate === endDate ? startDate : `${startDate} – ${endDate}`}):
              </span>
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: '5px', background: color + '18', border: `1px solid ${color}`, borderRadius: '16px', padding: '3px 12px', fontSize: '12px', fontWeight: 700, color }}>
                <Briefcase size={13} /> Jobs: {jCount}
              </span>
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: '5px', background: '#eff6ff', border: '1px solid #3b82f6', borderRadius: '16px', padding: '3px 12px', fontSize: '12px', fontWeight: 700, color: '#1d4ed8' }}>
                <FileText size={13} /> Submissions: {sCount}
              </span>
            </div>
          );
        })()}

        {/* ── Main Multi-Client / Individual Client Trend Chart ── */}
        {isLoading ? (
          <div className="ccr-chart-loading">Loading chart data…</div>
        ) : chartData.length === 0 ? (
          <div className="ccr-chart-empty">No trend data available for the selected period.</div>
        ) : (
          <ResponsiveContainer width="100%" height={320}>
            <BarChart data={chartData} margin={{ top: 24, right: 20, left: 0, bottom: 20 }}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
              <XAxis dataKey="name" tick={{ fill: '#64748b', fontSize: 11 }} axisLine={{ stroke: '#e2e8f0' }} tickLine={false} dy={8} />
              <YAxis tick={{ fill: '#64748b', fontSize: 11 }} axisLine={false} tickLine={false} allowDecimals={false} dx={-4} />
              <Tooltip
                contentStyle={{ borderRadius: '8px', border: 'none', boxShadow: '0 4px 12px rgba(0,0,0,0.12)', fontSize: '12px' }}
                cursor={{ fill: '#f8fafc' }}
                wrapperStyle={{ zIndex: 9999 }}
              />
              <Legend
                wrapperStyle={{ paddingTop: 20 }}
                content={() => (
                  <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: '8px', paddingTop: '16px' }}>
                    {chartKeys.slice(0, 10).map((clientName, idx) => {
                      const isActive = !selectedClient || selectedClient.name === clientName;
                      const color = COLORS[idx % COLORS.length];
                      return (
                        <div
                          key={clientName}
                          onClick={() => handleClientClick(clientName)}
                          style={{
                            display: 'flex', alignItems: 'center', gap: '6px',
                            cursor: 'pointer', padding: '4px 10px', borderRadius: '20px',
                            background: selectedClient?.name === clientName ? color + '18' : 'transparent',
                            border: selectedClient?.name === clientName ? `1.5px solid ${color}` : '1.5px solid transparent',
                            opacity: isActive ? 1 : 0.4,
                            transition: 'all 0.2s ease',
                            fontWeight: selectedClient?.name === clientName ? 700 : 400,
                            fontSize: '12px', color: '#475569', userSelect: 'none'
                          }}
                        >
                          <div style={{ width: 10, height: 10, borderRadius: '50%', background: color, flexShrink: 0 }} />
                          {clientName}
                        </div>
                      );
                    })}
                    {selectedClient && (
                      <div
                        onClick={() => setSelectedClient(null)}
                        style={{
                          display: 'flex', alignItems: 'center', gap: '4px',
                          cursor: 'pointer', padding: '4px 10px', borderRadius: '20px',
                          background: '#f1f5f9', fontSize: '12px', color: '#64748b',
                          border: '1px solid #e2e8f0', transition: 'all 0.2s ease', fontWeight: 500
                        }}
                      >
                        ✕ Clear filter
                      </div>
                    )}
                  </div>
                )}
              />
              {selectedClient ? (
                <>
                  <Bar
                    key={`${selectedClient.name}_jobs`}
                    dataKey={`${selectedClient.name}_jobs`}
                    name="Jobs"
                    fill="#3b5bfd"
                    radius={[3, 3, 0, 0]}
                    barSize={14}
                  >
                    <LabelList
                      dataKey={`${selectedClient.name}_jobs`}
                      position="top"
                      style={{ fill: '#007a6e', fontSize: 11, fontWeight: 700 }}
                      formatter={(val) => (val > 0 ? val : '')}
                    />
                  </Bar>
                  <Bar
                    key={selectedClient.name}
                    dataKey={selectedClient.name}
                    name="Submissions"
                    fill="#3b82f6"
                    radius={[3, 3, 0, 0]}
                    barSize={14}
                  >
                    <LabelList
                      dataKey={selectedClient.name}
                      position="top"
                      style={{ fill: '#1d4ed8', fontSize: 11, fontWeight: 700 }}
                      formatter={(val) => (val > 0 ? val : '')}
                    />
                  </Bar>
                </>
              ) : (
                chartKeys.slice(0, 10).map((key, i) => {
                  const color = COLORS[i % COLORS.length];
                  return (
                    <Bar
                      key={key}
                      dataKey={key}
                      name={key}
                      fill={color}
                      radius={[2, 2, 0, 0]}
                      barSize={6}
                      style={{ cursor: "pointer" }}
                      onClick={() => handleClientClick(key)}
                    />
                  );
                })
              )}
            </BarChart>
          </ResponsiveContainer>
        )}
      </div>

      {/* ── Total Client Trend + Detailed Breakdown Table ── */}
      <div className="ccr-bottom-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px', marginTop: '24px' }}>
        {/* Total Client Trend Area Chart */}
        <div style={{ background: 'white', borderRadius: '12px', overflow: 'hidden', border: '1px solid #e2e8f0', boxShadow: '0 1px 3px rgba(0,0,0,0.05)', display: 'flex', flexDirection: 'column' }}>
          <div style={{ background: '#26323f', padding: '14px 20px', color: 'white', display: 'flex', alignItems: 'center', gap: '8px', fontSize: '15px', fontWeight: 600 }}>
            <TrendingUp size={16} color="white" /> {selectedClient ? `${selectedClient.name} Trend` : 'Total Client Trend'}
          </div>
          <div style={{ padding: '20px 20px 20px 0', flex: 1, height: '300px' }}>
            {isLoading ? (
              <div className="ccr-chart-loading">Loading data…</div>
            ) : totalClientTrendData.length > 0 ? (
              <ResponsiveContainer width="100%" height={260}>
                <AreaChart data={totalClientTrendData} margin={{ top: 25, right: 30, left: 0, bottom: 10 }}>
                  <defs>
                    <linearGradient id="jobsTrendGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#3b5bfd" stopOpacity={0.25} />
                      <stop offset="95%" stopColor="#3b5bfd" stopOpacity={0} />
                    </linearGradient>
                    <linearGradient id="subsTrendGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.25} />
                      <stop offset="95%" stopColor="#3b82f6" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
                  <XAxis dataKey="name" axisLine={{ stroke: '#e2e8f0' }} tickLine={false} tick={{ fill: '#64748b', fontSize: 11 }} dy={8} />
                  <YAxis axisLine={false} tickLine={false} tick={{ fill: '#64748b', fontSize: 11 }} allowDecimals={false} dx={-4} />
                  <Tooltip
                    contentStyle={{ borderRadius: '8px', border: '1px solid #e2e8f0', boxShadow: '0 4px 12px rgba(0,0,0,0.08)', fontSize: '12px' }}
                    formatter={(val, name) => [val, name === "jobs" ? "Jobs" : "Submissions"]}
                  />
                  <Legend
                    wrapperStyle={{ paddingTop: 10, fontSize: 12 }}
                    formatter={(value) => value === "jobs" ? "Jobs" : "Submissions"}
                  />
                  <Area
                    type="monotone"
                    dataKey="jobs"
                    name="jobs"
                    stroke="#3b5bfd"
                    strokeWidth={2.5}
                    fill="url(#jobsTrendGrad)"
                    dot={{ r: 4, fill: '#3b5bfd', strokeWidth: 2, stroke: '#fff' }}
                    activeDot={{ r: 6, fill: '#3b5bfd', stroke: '#fff', strokeWidth: 2 }}
                  >
                    <LabelList dataKey="jobs" position="top" style={{ fill: '#007a6e', fontSize: 11, fontWeight: 700 }} formatter={(v) => (v > 0 ? v : '')} />
                  </Area>
                  <Area
                    type="monotone"
                    dataKey="submissions"
                    name="submissions"
                    stroke="#3b82f6"
                    strokeWidth={2.5}
                    fill="url(#subsTrendGrad)"
                    dot={{ r: 4, fill: '#3b82f6', strokeWidth: 2, stroke: '#fff' }}
                    activeDot={{ r: 6, fill: '#3b82f6', stroke: '#fff', strokeWidth: 2 }}
                  >
                    <LabelList dataKey="submissions" position="top" style={{ fill: '#1d4ed8', fontSize: 11, fontWeight: 700 }} formatter={(v) => (v > 0 ? v : '')} />
                  </Area>
                </AreaChart>
              </ResponsiveContainer>
            ) : (
              <div className="ccr-chart-empty">No trend data available.</div>
            )}
          </div>
        </div>

        {/* Detailed Breakdown Table */}
        <div style={{ background: 'white', borderRadius: '12px', padding: '20px', border: '1px solid #e2e8f0', boxShadow: '0 1px 3px rgba(0,0,0,0.05)', display: 'flex', flexDirection: 'column' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px', flexWrap: 'wrap', gap: '8px' }}>
            <h3 style={{ margin: 0, fontSize: '15px', color: '#1e293b', fontWeight: 600 }}>Detailed Breakdown</h3>
            
            {/* Filter Toggle Pills */}
            <div style={{ display: 'flex', gap: '4px', background: '#f8fafc', padding: '2px', borderRadius: '6px', border: '1px solid #e2e8f0' }}>
              <button
                type="button"
                onClick={() => setClientFilterStatus('all')}
                style={{
                  padding: '4px 10px', fontSize: '11px', borderRadius: '4px', border: 'none', cursor: 'pointer',
                  fontWeight: clientFilterStatus === 'all' ? 700 : 500,
                  background: clientFilterStatus === 'all' ? '#3b5bfd' : 'transparent',
                  color: clientFilterStatus === 'all' ? 'white' : '#64748b'
                }}
              >
                All ({clients.length})
              </button>
              <button
                type="button"
                onClick={() => setClientFilterStatus('with_subs')}
                style={{
                  padding: '4px 10px', fontSize: '11px', borderRadius: '4px', border: 'none', cursor: 'pointer',
                  fontWeight: clientFilterStatus === 'with_subs' ? 700 : 500,
                  background: clientFilterStatus === 'with_subs' ? '#3b5bfd' : 'transparent',
                  color: clientFilterStatus === 'with_subs' ? 'white' : '#64748b'
                }}
              >
                With Subs ({clientsWithSubsCount})
              </button>
              <button
                type="button"
                onClick={() => setClientFilterStatus('without_subs')}
                style={{
                  padding: '4px 10px', fontSize: '11px', borderRadius: '4px', border: 'none', cursor: 'pointer',
                  fontWeight: clientFilterStatus === 'without_subs' ? 700 : 500,
                  background: clientFilterStatus === 'without_subs' ? '#ef4444' : 'transparent',
                  color: clientFilterStatus === 'without_subs' ? 'white' : '#64748b'
                }}
              >
                ⚠️ Without Subs ({clientsWithoutSubsCount})
              </button>
            </div>
          </div>

          <div style={{ flex: 1, maxHeight: '300px', overflowY: 'auto' }}>
            {sortedClients.length === 0 ? (
              <div style={{ padding: '20px', textAlign: 'center', color: '#94a3b8', fontSize: '13px' }}>
                {clientFilterStatus === 'without_subs' ? 'No clients without submissions.' : 'No client data available.'}
              </div>
            ) : (
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                <thead>
                  <tr style={{ borderBottom: '2px solid #f1f5f9', textAlign: 'left' }}>
                    <th style={{ padding: '8px 12px', color: '#64748b', fontWeight: 600 }}>Client</th>
                    <th style={{ padding: '8px 12px', color: '#64748b', fontWeight: 600, textAlign: 'center' }}>Jobs</th>
                    <th style={{ padding: '8px 12px', color: '#64748b', fontWeight: 600, textAlign: 'center' }} title="Total candidate profiles sourced">Total Sourced</th>
                    <th style={{ padding: '8px 12px', color: '#0f766e', fontWeight: 700, textAlign: 'right' }} title="Profiles submitted to client">Submitted to Client</th>
                  </tr>
                </thead>
                <tbody>
                  {sortedClients.map((client, idx) => {
                    const hasZeroClientSubs = ((client.submittedToClientCount !== undefined ? client.submittedToClientCount : client.submissionCount) || 0) === 0;
                    return (
                      <tr
                        key={client.clientId || client.clientName || idx}
                        style={{ borderBottom: '1px solid #f8fafc', transition: 'background 0.15s ease', background: hasZeroClientSubs && clientFilterStatus === 'all' ? '#fffbeb' : 'transparent' }}
                        onMouseEnter={e => e.currentTarget.style.background = '#f8fafc'}
                        onMouseLeave={e => e.currentTarget.style.background = hasZeroClientSubs && clientFilterStatus === 'all' ? '#fffbeb' : 'transparent'}
                      >
                        <td style={{ padding: '10px 12px' }}>
                          <button
                            onClick={() => handleClientClick(client.clientName)}
                            style={{
                              background: 'none', border: 'none', padding: 0,
                              color: selectedClient?.name === client.clientName ? '#3b5bfd' : '#0284c7',
                              fontWeight: selectedClient?.name === client.clientName ? 700 : 600,
                              cursor: 'pointer', textAlign: 'left', fontSize: '13px'
                            }}
                          >
                            {client.clientName}
                          </button>
                          {hasZeroClientSubs && (
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                setMissingModalClient(client.clientName);
                              }}
                              style={{
                                marginLeft: '8px', fontSize: '10px', background: '#fef2f2',
                                color: '#dc2626', padding: '2px 8px', borderRadius: '4px',
                                fontWeight: 700, border: '1px solid #fecaca', cursor: 'pointer'
                              }}
                              title={`View unsubmitted jobs for ${client.clientName}`}
                            >
                              No Subs ↗
                            </button>
                          )}
                        </td>
                        <td style={{ padding: '10px 12px', textAlign: 'center' }}>
                          <span style={{ background: '#f1f5f9', color: '#475569', padding: '2px 8px', borderRadius: '12px', fontWeight: 600, fontSize: '12px' }}>
                            {client.jobCount ?? 0}
                          </span>
                        </td>
                        <td style={{ padding: '10px 12px', textAlign: 'center' }}>
                          <span style={{ background: '#f8fafc', color: '#64748b', padding: '2px 8px', borderRadius: '12px', fontWeight: 600, fontSize: '12px', border: '1px solid #e2e8f0' }}>
                            {client.submissionCount ?? 0}
                          </span>
                        </td>
                        <td style={{ padding: '10px 12px', textAlign: 'right' }}>
                          {hasZeroClientSubs ? (
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                setMissingModalClient(client.clientName);
                              }}
                              style={{
                                background: '#fef2f2', color: '#dc2626', padding: '3px 10px',
                                borderRadius: '12px', fontWeight: 700, fontSize: '12px',
                                border: '1px solid #fecaca', cursor: 'pointer'
                              }}
                              title={`View unsubmitted jobs for ${client.clientName}`}
                            >
                              0 ↗
                            </button>
                          ) : (
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                setSubmittedModalClient(client.clientName);
                              }}
                              style={{
                                background: '#eff6ff', color: '#1d4ed8', padding: '3px 10px',
                                borderRadius: '12px', fontWeight: 700, fontSize: '12px',
                                border: '1px solid #bfdbfe', cursor: 'pointer',
                                display: 'inline-flex', alignItems: 'center', gap: '4px',
                                transition: 'all 0.15s ease'
                              }}
                              title={`Click to view candidates submitted to ${client.clientName}`}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.background = '#dbeafe';
                                e.currentTarget.style.borderColor = '#93c5fd';
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.background = '#eff6ff';
                                e.currentTarget.style.borderColor = '#bfdbfe';
                              }}
                            >
                              {client.submittedToClientCount} <span style={{ fontSize: '10px' }}>↗</span>
                            </button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </div>

      {/* Missing Submissions Modal */}
      {missingModalClient && (
        <MissingSubmissionsModal
          startDate={startDate}
          endDate={endDate}
          region={regionFilter}
          client={missingModalClient === 'all' ? null : missingModalClient}
          creationType={creationType}
          onClose={() => setMissingModalClient(null)}
        />
      )}

      {/* Submitted to Client Modal */}
      {submittedModalClient && (
        <ClientSubmissionsModal
          startDate={startDate}
          endDate={endDate}
          region={regionFilter}
          client={submittedModalClient === 'all' ? null : submittedModalClient}
          creationType={creationType}
          onClose={() => setSubmittedModalClient(null)}
        />
      )}

    </div>
  );
};

export default ClientCountReport;


